import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  TablePagination,
  Menu,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { useLanguage } from '../../utils/i18n';

const headerStyle = {
  backgroundColor: '#5166AE',
  color: 'white',
  border: '1px solid #ddd',
  padding: '12px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};

const cellStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: '0.875rem'
};

const getStickyStyle = (leftPos, z, bgColor) => ({
  position: 'sticky',
  left: leftPos,
  zIndex: z,
  backgroundColor: bgColor
});

export default function RitaseSummaryTable({
  title,
  columns,
  rows,
  loading,
  paginationModel,
  onPaginationModelChange,
  tableSearchQuery,
  setTableSearchQuery
}) {
  const { t } = useLanguage();
  const [isExporting, setIsExporting] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const getFormattedDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const getCellValue = (row, col, index) => {
    try {
      if (col.field === '__no__' || col.field === 'no') return index + 1;

      if (typeof col.valueGetter === 'function') {
        if (col.valueGetter.length > 1) {
          return col.valueGetter(row[col.field], row, col, null) ?? '';
        } else {
          return col.valueGetter({ row, value: row[col.field], colDef: col, id: index }) ?? '';
        }
      }

      let val = row[col.field];
      if (val === undefined || val === null) return '';
      if (typeof val === 'object') {
        if (val.props && val.props.children !== undefined) {
          return val.props.children;
        }
        return '';
      }
      return val;
    } catch {
      return row[col.field] ?? '';
    }
  };

  const exportToCSV = async () => {
    if (!rows || rows.length === 0) return;
    setIsExporting(true);
    try {
      const formattedData = rows.map((row, index) => {
        const rowValues = {
          No: index + 1
        };
        columns.forEach((col) => {
          let val = getCellValue(row, col, index);
          if (Array.isArray(val)) val = val.join(', ');
          rowValues[col.headerName || col.field] = val;
        });
        return rowValues;
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${(title || 'ritase-summary').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    if (!rows || rows.length === 0) return;
    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(title || t('reports.ritaseSummary', 'Ritase Summary'));

      // Extract headers from columns
      const headerNames = ['No', ...columns.map((c) => c.headerName || c.field)];
      sheet.addRow(headerNames);

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5166AE' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Set column widths
      sheet.columns.forEach((column, index) => {
        column.width = index === 0 ? 10 : index === 1 ? 25 : 15;
      });

      // Data Rows
      rows.forEach((row, index) => {
        const rowData = [index + 1];
        columns.forEach((col) => {
          const val = getCellValue(row, col, index);
          rowData.push(val !== undefined && val !== null ? val : '');
        });
        const excelRow = sheet.addRow(rowData);
        excelRow.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Add borders
      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title || 'ritase-summary').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    if (!rows || rows.length === 0) return;
    setIsExporting(true);
    try {
      const doc = new jsPDF('landscape');

      const tableColumn = ['No', ...columns.map((col) => col.headerName || col.field)];

      const tableRows = rows.map((row, index) => {
        return [
          index + 1,
          ...columns.map((col) => {
            let val = getCellValue(row, col, index);
            if (Array.isArray(val)) val = val.join('\n');
            return val !== null && val !== undefined ? String(val) : '-';
          })
        ];
      });

      if (title) {
        doc.setFontSize(16);
        doc.text(title, 14, 15);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: title ? 20 : 14,
        theme: 'grid',
        styles: { fontSize: 7, halign: 'center' },
        headStyles: { fillColor: [81, 102, 174] }, // #5166AE
        columnStyles: {
          0: { cellWidth: 15 },
          1: { halign: 'left', cellWidth: 35 }
        }
      });

      doc.save(`${(title || 'ritase-summary').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const stickyCols = {
    no: { left: 0, width: 50 },
    licensePlate: { left: 50, width: 180 }
  };

  const paginatedRows = rows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  return (
    <Paper sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder={t('reportModule.searchInTable', 'Search in table...')}
            value={tableSearchQuery}
            onChange={(e) => setTableSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleExportClick}
            disabled={isExporting || rows.length === 0}
            aria-controls={exportOpen ? 'export-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={exportOpen ? 'true' : undefined}
          >
            {isExporting ? t('general.exporting', 'Exporting...') : t('general.export', 'Export')}
          </Button>
          <Menu id="export-menu" anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
            <MenuItem onClick={exportToCSV}>{t('table.export.csv', 'Export to CSV')}</MenuItem>
            <MenuItem onClick={exportToExcel}>{t('table.export.excel', 'Export to Excel')}</MenuItem>
            <MenuItem onClick={exportToPDF}>{t('table.export.pdf', 'Export to PDF')}</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Manual Table */}
      <Box
        sx={{ flexGrow: 1, width: '100%', minHeight: 0, overflowX: 'auto', overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1 }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              <th
                style={{
                  ...headerStyle,
                  ...getStickyStyle(stickyCols.no.left, 4, '#5166AE'),
                  minWidth: stickyCols.no.width,
                  maxWidth: stickyCols.no.width,
                  boxSizing: 'border-box'
                }}
              >
                {t('dataTable.no', 'No')}
              </th>
              {columns.map((col) => {
                const isSticky = stickyCols[col.field];
                const width = isSticky ? isSticky.width : 80;
                const customStyle = isSticky
                  ? {
                      ...headerStyle,
                      ...getStickyStyle(isSticky.left, 4, '#5166AE'),
                      minWidth: width,
                      maxWidth: width,
                      boxSizing: 'border-box'
                    }
                  : { ...headerStyle, minWidth: width, boxSizing: 'border-box' };
                return (
                  <th key={col.field} style={customStyle}>
                    {col.headerName}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} style={{ ...cellStyle, padding: '20px' }}>
                  <CircularProgress />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} style={{ ...cellStyle, padding: '20px' }}>
                  {t('reports.noDataFound', 'No data found')}
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, index) => {
                const rowNo = paginationModel.page * paginationModel.pageSize + index + 1;
                return (
                  <tr key={row.id || index}>
                    <td
                      style={{
                        ...cellStyle,
                        ...getStickyStyle(stickyCols.no.left, 1, '#fff'),
                        minWidth: stickyCols.no.width,
                        maxWidth: stickyCols.no.width,
                        boxSizing: 'border-box'
                      }}
                    >
                      {rowNo}
                    </td>
                    {columns.map((col) => {
                      const isSticky = stickyCols[col.field];
                      const width = isSticky ? isSticky.width : 80;
                      const customStyle = isSticky
                        ? {
                            ...cellStyle,
                            ...getStickyStyle(isSticky.left, 1, '#fff'),
                            minWidth: width,
                            maxWidth: width,
                            boxSizing: 'border-box'
                          }
                        : { ...cellStyle, minWidth: width, boxSizing: 'border-box' };

                      let val = row[col.field];
                      if (col.renderCell) {
                        val = col.renderCell({ value: val, row });
                      }

                      return (
                        <td key={col.field} style={customStyle}>
                          {val !== undefined && val !== null ? val : '-'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      <TablePagination
        component="div"
        count={rows.length}
        page={paginationModel.page}
        onPageChange={(e, newPage) => onPaginationModelChange((prev) => ({ ...prev, page: newPage }))}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={(e) => onPaginationModelChange({ page: 0, pageSize: parseInt(e.target.value, 10) })}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
}
